'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
interface RollingChartQuery<TData> {
  data: TData | undefined;
  isFetching: boolean;
  refetch: (options: {
    cancelRefetch: boolean;
  }) => unknown;
}

/**
 * Refetch a rolling PnL chart when the page becomes visible again, and once
 * when it mounts over cached data. Market moves change the open-leg estimate
 * without any NATS event for the wallet, the realtime query never goes stale,
 * and a same-tab navigation back fires no visibility event, so these are the
 * only refreshes a returning viewer gets. A first load or a refresh already in
 * flight is left alone. Focus and visibility often fire together;
 * `cancelRefetch: false` joins the in-flight request instead of starting a
 * second one.
 */
export function useRefetchWhenVisible(query) {
  const $ = _c(10);
  const {
    refetch
  } = query;
  let t0;
  if ($[0] !== query.data || $[1] !== query.isFetching) {
    t0 = () => query.data !== undefined && !query.isFetching;
    $[0] = query.data;
    $[1] = query.isFetching;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [cachedAtMount] = useState(t0);
  let t1;
  let t2;
  if ($[3] !== cachedAtMount || $[4] !== refetch) {
    t1 = () => {
      if (cachedAtMount && document.visibilityState === "visible") {
        refetch({
          cancelRefetch: false
        });
      }
    };
    t2 = [cachedAtMount, refetch];
    $[3] = cachedAtMount;
    $[4] = refetch;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[7] !== refetch) {
    t3 = () => {
      const refetchWhenVisible = function refetchWhenVisible() {
        if (document.visibilityState === "visible") {
          refetch({
            cancelRefetch: false
          });
        }
      };
      window.addEventListener("focus", refetchWhenVisible);
      window.addEventListener("pageshow", refetchWhenVisible);
      document.addEventListener("visibilitychange", refetchWhenVisible);
      return () => {
        window.removeEventListener("focus", refetchWhenVisible);
        window.removeEventListener("pageshow", refetchWhenVisible);
        document.removeEventListener("visibilitychange", refetchWhenVisible);
      };
    };
    t4 = [refetch];
    $[7] = refetch;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
}